import { useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { QuickAddButton } from './QuickAddButton';
import { ToastContainer, ConfirmDialog, SessionTimeoutModal } from '@/components/ui';
import { useAuthStore, useItemStore, useUIStore, useSettingsStore } from '@/stores';
import { useSessionTimeout } from '@/hooks';
import { getUserProfile, type ItemType } from '@perfin/shared';

export function AppLayout() {
  const navigate = useNavigate();
  const { user, profile } = useAuthStore();
  const { subscribeToAllItems } = useItemStore();
  const { sidebarCollapsed, activeModal, modalData, closeModal } = useUIStore();
  const { initialize, setupNewUserOrganization, currentUserRole } = useSettingsStore();

  // Session timeout management
  const {
    showWarning: showSessionWarning,
    remainingMs: sessionRemainingMs,
    refreshSession,
    logoutNow,
    dismissWarning: dismissSessionWarning,
  } = useSessionTimeout();

  // Handle quick-add navigation (replaces ItemFormModal)
  useEffect(() => {
    if (activeModal === 'itemForm') {
      const type = (modalData?.defaultType as ItemType) || 'task';
      closeModal();
      navigate(`/items/new?mode=create&type=${type}`);
    }
  }, [activeModal, modalData, closeModal, navigate]);

  // Subscribe to items when user is authenticated AND profile is loaded
  // (Firestore rules require organizationId which comes from profile)
  useEffect(() => {
    if (user?.uid && profile?.organizationId && profile?.role) {
      const unsubscribe = subscribeToAllItems(user.uid, profile.organizationId, profile.role);
      return () => unsubscribe();
    }
  }, [user?.uid, profile?.organizationId, profile?.role, subscribeToAllItems]);

  // Auto-create organization and initialize settings store
  useEffect(() => {
    if (!user || !profile) return;

    let unsubscribe: (() => void) | undefined;

    const initSettings = async () => {
      try {
        let orgId = profile.organizationId;

        let role = profile.role;

        if (!orgId) {
          // Create org + seed categories for new user (they become admin)
          orgId = await setupNewUserOrganization(
            user.uid,
            profile.displayName || user.displayName || undefined
          );
          role = 'admin'; // New org creators are always admin

          // Refresh auth state so other effects can run (e.g., subscribeToAllItems)
          try {
            await user.getIdToken(true);
            const updatedProfile = await getUserProfile(user.uid);
            useAuthStore.setState({ profile: updatedProfile });
          } catch (refreshError) {
            console.error('Failed to refresh profile after org creation:', refreshError);
            // Continue anyway - settings will work, items subscription will retry on next render
          }
        }

        // Role should always exist at this point (either from profile or set to 'admin' above)
        if (!role) {
          console.error('User has no role assigned');
          return;
        }

        unsubscribe = initialize(orgId, user.uid, role);
      } catch (error) {
        console.error('Failed to initialize settings:', error);
      }
    };

    initSettings();

    return () => {
      unsubscribe?.();
    };
  }, [user, profile, initialize, setupNewUserOrganization]);

  // Don't render main content until settingsStore has initialized with role
  const isInitializing = profile?.organizationId && !currentUserRole;

  return (
    <div className="h-screen bg-gray-50 overflow-hidden">
      <Sidebar />

      <div
        className={clsx(
          'h-full flex flex-col transition-all duration-300',
          // Mobile: no margin (sidebar is overlay)
          // Desktop: margin based on collapsed state
          sidebarCollapsed ? 'md:ml-16' : 'md:ml-64'
        )}
      >
        <div className="flex-shrink-0">
          <Header />
        </div>

        <main className="flex-1 p-6 min-h-0 overflow-y-auto">
          {isInitializing ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : (
            <Outlet />
          )}
        </main>
      </div>

      <QuickAddButton />
      <ToastContainer />
      <ConfirmDialog />

      {/* Session Timeout Warning Modal */}
      <SessionTimeoutModal
        isOpen={showSessionWarning}
        remainingMs={sessionRemainingMs}
        onStayLoggedIn={refreshSession}
        onLogoutNow={logoutNow}
        onDismiss={dismissSessionWarning}
      />
    </div>
  );
}
